/** True when fetch never reached the Cursor proxy (offline, ad blocker, wrong origin). */
export function isCursorNetworkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return isNetworkFetchFailure(err, msg);
}

/** Cursor API 409 — a run is already in progress for this agent. */
export function isCursorAgentActiveRunError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return msg.includes('active run') || msg.includes('still working on');
}

/** Poll window expired while the cloud agent is still running. */
export function isCursorAgentStillRunningError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  return msg.includes('timed out') && msg.includes('still running');
}

/** Short copy for the status line under the composer. */
export function formatCursorFetchErrorBrief(err: unknown): string {
  if (isCursorNetworkError(err)) {
    return 'Could not reach the Cursor API — check connection, ad blockers, and API key in Config.';
  }
  if (isCursorAgentActiveRunError(err)) {
    return 'Agent is still working on your last message…';
  }
  if (isCursorAgentStillRunningError(err)) {
    return 'Agent is still running — leave this open or check back in a minute.';
  }
  const msg = err instanceof Error ? err.message : String(err);
  return msg.trim() || 'Request failed';
}

/** Turn browser fetch failures into actionable copy for the AI Agent chat. */
export function formatCursorFetchError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (isNetworkFetchFailure(err, msg)) {
    return (
      'Could not reach the Cursor API proxy at /api/cursor-agent on this site. ' +
      'Local dev: run "cd app && npm run dev" and open that URL (not a file:// or old build). ' +
      'Live site: wait for Netlify deploy, disable ad blockers for this domain, then retry. ' +
      'Also confirm Tools → Config → AI Agent has a valid Cloud Agents API key.'
    );
  }
  return msg.trim() || 'Request failed';
}

function isNetworkFetchFailure(err: unknown, message: string): boolean {
  if (!(err instanceof TypeError)) return false;
  const m = message.toLowerCase();
  return (
    m.includes('networkerror') ||
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('network request failed')
  );
}

export type CursorAgentRunErrorHandling = {
  /** Keep Send disabled and show streaming thinking state. */
  keepBusy: boolean;
  /** Keep pending-run marker so reload can resume polling. */
  keepPending: boolean;
  /** Restore the thinking bubble (do not show a chat error). */
  keepThinking: boolean;
};

/** How the AI Agent chat UI should react to a failed send/resume. */
export function cursorAgentRunErrorHandling(err: unknown): CursorAgentRunErrorHandling {
  if (isCursorNetworkError(err)) {
    return { keepBusy: false, keepPending: true, keepThinking: true };
  }
  if (isCursorAgentActiveRunError(err) || isCursorAgentStillRunningError(err)) {
    return { keepBusy: true, keepPending: true, keepThinking: true };
  }
  return { keepBusy: false, keepPending: false, keepThinking: false };
}
